import { useState, useEffect, FormEvent } from 'react';
import { getSettings, updateSettings, getSports, SportId, resetThemeColorsFromBadge, changePassword, getBadgeUploadUrl, updateBadge, Settings as SettingsType, getStripeConnectStatus, createStripeConnect, createStripeAccountLink, getStripeDashboardLink, StripeConnectStatus, getSubscriptionStatus, createCustomerPortalSession, SubscriptionStatus, getAdminUsers, inviteAdminUser, removeAdminUser, AdminUser, AdminUsersResponse, exportClubData, deleteAccount } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { invalidateSportConfig } from '../hooks/useSportConfig';
import './Settings.css';

import { PLATFORM_DOMAIN } from '../platform';
const PUBLIC_API_URL = import.meta.env.VITE_API_URL || 'https://api.allgrassroots.invalid';

async function fetchClubBadgeUrl(clubId: string): Promise<string> {
  const response = await fetch(`${PUBLIC_API_URL}/api/v1/clubs/${clubId}`);
  if (!response.ok) return '';
  const data = await response.json();
  return (data?.badgeUrl as string) || '';
}

function Settings() {
  const { clubId, plan, email: currentUserEmail, isOwner: isOwnerFromJwt, forcePasswordChange, clearForcePasswordChange, features, logout } = useAuth();

  // ── Danger zone (export + self-service deletion) ──
  const [isExporting, setIsExporting] = useState(false);
  const [deleteArmed, setDeleteArmed] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [dangerError, setDangerError] = useState('');

  const handleExportData = async () => {
    setIsExporting(true);
    setDangerError('');
    try {
      const bundle = await exportClubData();
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${clubId || 'club'}-allgrassroots-export.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDangerError(err instanceof Error ? err.message : 'Export failed, try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setIsDeletingAccount(true);
    setDangerError('');
    try {
      await deleteAccount();
      // The club (and this login) no longer exist, end the session.
      logout();
      window.location.href = '/';
    } catch (err) {
      setDangerError(err instanceof Error ? err.message : 'Deletion failed, contact hello@allgrassroots.com.');
      setIsDeletingAccount(false);
    }
  };
  // Preview mode, when the operator flag features.preview is on AND
  // the club is paid, every Save button on this page routes to a draft
  // rather than going live. The amber banner at the top of the page
  // (below the page-header) makes that obvious so the operator isn't
  // surprised that Save doesn't immediately change the live site.
  const previewMode = features?.preview === true && (plan === 'club' || plan === 'pro');
  const saveButtonLabel = (verb: string) => previewMode ? `${verb} to draft` : verb;
  const isFree = plan === 'free';

  const [settings, setSettings] = useState<SettingsType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [formData, setFormData] = useState({
    siteName: '',
    siteDescription: '',
    contactEmail: '',
    contactPhone: '',
    facebook: '',
    twitter: '',
    instagram: '',
    maintenanceMode: false,
    teamCardCoaches: 'all' as 'all' | 'lead',
    sport: 'football' as SportId,
    gaTrackingId: '',
    // null = Classic (default look, no override).
    // 'matchday' = Matchday theme (dark/parallax).
    // 'newsfeed' = Newsfeed theme (Cambridge-style light).
    themeName: null as 'matchday' | 'newsfeed' | null,
  });

  // Theme picker, Pro tier only
  const [themeSaving, setThemeSaving] = useState(false);
  const [themeError, setThemeError] = useState('');
  const [themeSuccess, setThemeSuccess] = useState('');

  // Brand colours, free for all tiers. Admin picks Primary + Secondary;
  // server derives the 4 light/dark variants via HSL adjustment.
  const [primaryColor, setPrimaryColor] = useState('#22c55e');
  const [secondaryColor, setSecondaryColor] = useState('#1a1a1a');
  const [hasBadge, setHasBadge] = useState(false);
  const [coloursSaving, setColoursSaving] = useState(false);
  const [coloursResetting, setColoursResetting] = useState(false);
  const [coloursError, setColoursError] = useState('');
  const [coloursSuccess, setColoursSuccess] = useState('');

  // Badge state
  const [badgeUrl, setBadgeUrl] = useState('');
  const [isUploadingBadge, setIsUploadingBadge] = useState(false);
  const [badgeError, setBadgeError] = useState('');
  const [badgeSuccess, setBadgeSuccess] = useState('');

  // Change password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Stripe Connect state
  const [stripeStatus, setStripeStatus] = useState<StripeConnectStatus | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [stripeError, setStripeError] = useState('');
  const [stripeMessage, setStripeMessage] = useState('');

  // Subscription state
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [isManagingSub, setIsManagingSub] = useState(false);

  // Admin users state
  const [adminUsers, setAdminUsers] = useState<AdminUsersResponse | null>(null);
  const [isLoadingAdmins, setIsLoadingAdmins] = useState(false);
  const [adminError, setAdminError] = useState('');
  const [adminSuccess, setAdminSuccess] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const [removingEmail, setRemovingEmail] = useState<string | null>(null);

  const [sports, setSports] = useState<{ id: SportId; label: string }[]>([]);
  useEffect(() => {
    getSports().then(setSports).catch(() => { /* keep the current value as the only option */ });
  }, []);

  useEffect(() => {
    loadSettings();
    if (clubId) {
      fetchClubBadgeUrl(clubId).then((url) => {
        if (url) setBadgeUrl(url);
      });
    }
    // Load Stripe Connect status for all plans (shop is available on all plans)
    loadStripeStatus();
    // Load subscription status
    loadSubscriptionStatus();
    // Load admin users
    loadAdminUsers();
    // Handle Stripe redirect URL params
    const params = new URLSearchParams(window.location.search);
    const stripeParam = params.get('stripe');
    if (stripeParam === 'success') {
      setStripeMessage('Stripe account connected! Checking status...');
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
      // Reload status after a short delay (Stripe may take a moment)
      setTimeout(() => loadStripeStatus(), 1500);
    } else if (stripeParam === 'refresh') {
      setStripeError('Stripe onboarding was not completed. Please try again.');
      window.history.replaceState({}, '', window.location.pathname);
    }
    // If redirected here to force password change, scroll to the password section
    const changeParam = params.get('changePassword');
    if (changeParam === '1') {
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => {
        const el = document.getElementById('change-password-section');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 400);
    }
  }, [clubId]);

  const loadAdminUsers = async () => {
    setIsLoadingAdmins(true);
    try {
      const data = await getAdminUsers();
      setAdminUsers(data);
    } catch (err) {
      console.error('Failed to load admin users:', err);
    } finally {
      setIsLoadingAdmins(false);
    }
  };

  const handleInviteAdmin = async (e: FormEvent) => {
    e.preventDefault();
    setAdminError('');
    setAdminSuccess('');
    setIsInviting(true);

    try {
      await inviteAdminUser(inviteEmail, inviteName);
      setAdminSuccess(`Invite sent to ${inviteEmail}`);
      setInviteEmail('');
      setInviteName('');
      setShowInviteModal(false);
      await loadAdminUsers();
    } catch (err) {
      setAdminError(err instanceof Error ? err.message : 'Failed to send invite');
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemoveAdmin = async (emailToRemove: string) => {
    if (!window.confirm(`Remove ${emailToRemove} from this club's admin team?`)) return;
    setAdminError('');
    setAdminSuccess('');
    setRemovingEmail(emailToRemove);

    try {
      await removeAdminUser(emailToRemove);
      setAdminSuccess(`${emailToRemove} has been removed`);
      await loadAdminUsers();
    } catch (err) {
      setAdminError(err instanceof Error ? err.message : 'Failed to remove admin');
    } finally {
      setRemovingEmail(null);
    }
  };

  const loadStripeStatus = async () => {
    try {
      const status = await getStripeConnectStatus();
      setStripeStatus(status);
      if (status.connected && status.chargesEnabled) {
        setStripeMessage('');
      }
    } catch (err) {
      console.error('Failed to load Stripe status:', err);
    }
  };

  const loadSubscriptionStatus = async () => {
    try {
      const status = await getSubscriptionStatus();
      setSubscription(status);
    } catch (err) {
      console.error('Failed to load subscription status:', err);
    }
  };

  const handleManageSubscription = async () => {
    setIsManagingSub(true);
    try {
      const { url } = await createCustomerPortalSession();
      window.location.href = url;
    } catch (err) {
      setStripeError(err instanceof Error ? err.message : 'Failed to open subscription management');
      setIsManagingSub(false);
    }
  };

  const handleConnectStripe = async () => {
    setIsConnecting(true);
    setStripeError('');
    try {
      const result = await createStripeConnect();
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (err) {
      setStripeError(err instanceof Error ? err.message : 'Failed to start Stripe setup');
      setIsConnecting(false);
    }
  };

  const handleCompleteStripeSetup = async () => {
    setIsConnecting(true);
    setStripeError('');
    try {
      const result = await createStripeAccountLink();
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (err) {
      setStripeError(err instanceof Error ? err.message : 'Failed to generate setup link');
      setIsConnecting(false);
    }
  };

  const handleViewStripeDashboard = async () => {
    try {
      const result = await getStripeDashboardLink();
      if (result.url) {
        window.open(result.url, '_blank');
      }
    } catch (err) {
      setStripeError(err instanceof Error ? err.message : 'Failed to open Stripe dashboard');
    }
  };

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const data = await getSettings();
      setSettings(data);
      setFormData({
        siteName: data.siteName || '',
        siteDescription: data.siteDescription || '',
        contactEmail: data.contactEmail || '',
        contactPhone: data.contactPhone || '',
        facebook: data.socialMedia?.facebook || '',
        twitter: data.socialMedia?.twitter || '',
        instagram: data.socialMedia?.instagram || '',
        maintenanceMode: data.maintenanceMode || false,
        teamCardCoaches: data.teamCardCoaches === 'lead' ? 'lead' : 'all',
        sport: data.sport || 'football',
        gaTrackingId: data.gaTrackingId || '',
        themeName: (data.themeName === 'matchday' || data.themeName === 'newsfeed') ? data.themeName : null,
      });
      if (data.themeColors) {
        setPrimaryColor(data.themeColors.primaryColor);
        setSecondaryColor(data.themeColors.secondaryColor);
      }
      setHasBadge(!!data.hasBadge);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Brand colours handlers ───────────────────────────────────────
  const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

  const handleSaveColours = async () => {
    setColoursError('');
    setColoursSuccess('');
    if (!HEX_RE.test(primaryColor) || !HEX_RE.test(secondaryColor)) {
      setColoursError('Both colours must be valid hex codes (e.g. #1256aa).');
      return;
    }
    setColoursSaving(true);
    try {
      await updateSettings({ themeColors: { primaryColor, secondaryColor } });
      setColoursSuccess(previewMode
        ? `Saved to draft. Refresh ${clubId}-preview.${PLATFORM_DOMAIN} to see the new colours. Use "Publish all" in the amber bar to push them live.`
        : 'Saved. Your website + emails will reflect the new colours within a minute (CDN cache).');
      setTimeout(() => setColoursSuccess(''), 6000);
    } catch (err) {
      setColoursError(err instanceof Error ? err.message : 'Failed to save colours');
    } finally {
      setColoursSaving(false);
    }
  };

  const handleResetColoursToBadge = async () => {
    if (!hasBadge) {
      setColoursError('Upload a badge first, we extract colours from your badge image.');
      return;
    }
    if (!confirm('Reset to the colours auto-extracted from your badge? Any custom colours you set will be lost.')) {
      return;
    }
    setColoursError('');
    setColoursSuccess('');
    setColoursResetting(true);
    try {
      const updated = await resetThemeColorsFromBadge();
      if (updated.themeColors) {
        setPrimaryColor(updated.themeColors.primaryColor);
        setSecondaryColor(updated.themeColors.secondaryColor);
      }
      setColoursSuccess('Reset to badge colours. Your website + emails will update within a minute.');
      setTimeout(() => setColoursSuccess(''), 6000);
    } catch (err) {
      setColoursError(err instanceof Error ? err.message : 'Failed to reset colours');
    } finally {
      setColoursResetting(false);
    }
  };

  // WCAG contrast ratio against white, drives the readability hint
  // shown next to each picker. Mirrors the website's contrastClamp.ts
  // algorithm so the UX warning matches the runtime auto-fix.
  const contrastVsWhite = (hex: string): number => {
    if (!HEX_RE.test(hex)) return 0;
    let h = hex.replace(/^#/, '');
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    const r = parseInt(h.slice(0, 2), 16) / 255;
    const g = parseInt(h.slice(2, 4), 16) / 255;
    const b = parseInt(h.slice(4, 6), 16) / 255;
    const ch = (c: number) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    const lum = 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
    return (1.0 + 0.05) / (lum + 0.05);
  };
  const contrastBadge = (hex: string): { label: string; color: string } => {
    const r = contrastVsWhite(hex);
    if (r >= 7) return { label: 'Excellent on white (AAA)', color: '#15803d' };
    if (r >= 4.5) return { label: 'Good on white (AA)', color: '#15803d' };
    if (r >= 3) return { label: 'Borderline on white, auto-darkened for text', color: '#92400e' };
    return { label: 'Very pale on white, auto-darkened for text', color: '#92400e' };
  };

  const handleBadgeUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setBadgeError('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setBadgeError('Image must be less than 5MB');
      return;
    }

    setBadgeError('');
    setBadgeSuccess('');
    setIsUploadingBadge(true);

    try {
      // 1. Get presigned URL for badges bucket
      const { uploadUrl, publicUrl } = await getBadgeUploadUrl(file.name, file.type);

      // 2. Upload to S3
      await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      // 3. Update badge (runs Rekognition + updates club record)
      await updateBadge(publicUrl);

      // 4. Update local state
      setBadgeUrl(publicUrl);
      setBadgeSuccess('Badge updated successfully! Theme colours will be extracted automatically.');
    } catch (err) {
      setBadgeError(err instanceof Error ? err.message : 'Failed to upload badge');
    } finally {
      setIsUploadingBadge(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsSaving(true);

    try {
      const updatedSettings = {
        siteName: formData.siteName,
        siteDescription: formData.siteDescription,
        contactEmail: formData.contactEmail,
        contactPhone: formData.contactPhone,
        socialMedia: {
          facebook: formData.facebook || undefined,
          twitter: formData.twitter || undefined,
          instagram: formData.instagram || undefined,
        },
        maintenanceMode: formData.maintenanceMode,
        teamCardCoaches: formData.teamCardCoaches,
        sport: formData.sport,
        gaTrackingId: formData.gaTrackingId || undefined,
      };

      await updateSettings(updatedSettings);
      invalidateSportConfig();
      setSuccessMessage('Settings updated successfully');
      await loadSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update settings');
    } finally {
      setIsSaving(false);
    }
  };

  // Theme is saved on selection (no need to click main Save button),
  // simpler UX since it's a single one-of-N choice with instant feedback.
  // `null` represents Classic = no theme override → lambda REMOVEs the
  // field from DDB and the website falls back to the default look.
  const handleSaveTheme = async (themeName: 'matchday' | 'newsfeed' | null) => {
    setThemeError('');
    setThemeSuccess('');
    setThemeSaving(true);
    try {
      await updateSettings({ themeName });
      setFormData((prev) => ({ ...prev, themeName }));
      setThemeSuccess(`Theme saved. Your website will reflect the new look on the next page load.`);
      setTimeout(() => setThemeSuccess(''), 4000);
    } catch (err) {
      setThemeError(err instanceof Error ? err.message : 'Failed to save theme');
    } finally {
      setThemeSaving(false);
    }
  };

  const handleChangePassword = async (e: FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }

    setIsChangingPassword(true);
    try {
      await changePassword(currentPassword, newPassword);
      setPasswordSuccess('Password changed successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      // If this was a forced password change, clear the flag
      if (forcePasswordChange) {
        clearForcePasswordChange();
      }
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Failed to change password.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateShort = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return <div className="loading">Loading settings...</div>;
  }

  const adminLimit = adminUsers?.limit ?? 1;
  const adminCount = adminUsers?.count ?? 0;
  const atAdminLimit = adminCount >= adminLimit;

  // Derive isOwner from JWT role OR from admin list (handles legacy sole-admin clubs)
  const myAdminRecord = adminUsers?.admins.find(a => a.email === currentUserEmail);
  const isOwner = isOwnerFromJwt || myAdminRecord?.role === 'owner';

  return (
    <div className="settings-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">Configure site preferences</p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {previewMode && (
        <div className="settings-preview-notice">
          <strong>Preview mode is on.</strong> Saves on this page (site info,
          contact details, social links, brand colours, theme) go to your
          <em> draft</em>. Use <strong>Publish all</strong> in the amber bar
          at the top to push your changes to the live site, or
          <strong> Discard</strong> to throw them away.
        </div>
      )}

      {/* Force password change banner */}
      {forcePasswordChange && (
        <div className="admin-force-pw-banner">
          <strong>Action required:</strong> Please change your temporary password below before continuing.
        </div>
      )}

      <div className="settings-container">
        <form onSubmit={handleSubmit} className="settings-form">
          <div className="settings-section">
            <h2 className="section-title">Club Badge</h2>
            <p className="form-hint">Your club badge appears on the hero section, browser tab, and footer.</p>

            {badgeError && <div className="error-message">{badgeError}</div>}
            {badgeSuccess && <div className="success-message">{badgeSuccess}</div>}

            <div className="badge-upload-area">
              {badgeUrl && (
                <div className="badge-preview">
                  <img src={badgeUrl} alt="Current badge" className="badge-preview-img" />
                </div>
              )}
              <div className="badge-upload-actions">
                <label className="btn btn-secondary" style={{ cursor: isUploadingBadge ? 'wait' : 'pointer' }}>
                  {isUploadingBadge ? 'Uploading...' : badgeUrl ? 'Change Badge' : 'Upload Badge'}
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    disabled={isUploadingBadge}
                    onChange={handleBadgeUpload}
                  />
                </label>
                <p className="form-hint">PNG or JPG, max 5MB. Image will be checked for appropriate content.</p>
              </div>
            </div>
          </div>

          <div className="settings-section">
            <h2 className="section-title">General Information</h2>

            <div className="form-group">
              <label htmlFor="siteName" className="form-label">Site Name</label>
              <input
                id="siteName"
                type="text"
                className="form-input"
                value={formData.siteName}
                onChange={(e) => setFormData({ ...formData, siteName: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="siteDescription" className="form-label">Site Description</label>
              <textarea
                id="siteDescription"
                className="form-textarea"
                value={formData.siteDescription}
                onChange={(e) => setFormData({ ...formData, siteDescription: e.target.value })}
                required
                rows={4}
              />
            </div>
          </div>

          <div className="settings-section">
            <h2 className="section-title">Contact Information</h2>

            <div className="form-group">
              <label htmlFor="contactEmail" className="form-label">Contact Email</label>
              <input
                id="contactEmail"
                type="email"
                className="form-input"
                value={formData.contactEmail}
                onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="contactPhone" className="form-label">Contact Phone</label>
              <input
                id="contactPhone"
                type="tel"
                className="form-input"
                value={formData.contactPhone}
                onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="settings-section">
            <h2 className="section-title">Social Media</h2>

            <div className="form-group">
              <label htmlFor="facebook" className="form-label">Facebook URL</label>
              <input
                id="facebook"
                type="url"
                className="form-input"
                value={formData.facebook}
                onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                placeholder="https://facebook.com/yourclub"
              />
            </div>

            <div className="form-group">
              <label htmlFor="twitter" className="form-label">Twitter URL</label>
              <input
                id="twitter"
                type="url"
                className="form-input"
                value={formData.twitter}
                onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
                placeholder="https://twitter.com/yourclub"
              />
            </div>

            <div className="form-group">
              <label htmlFor="instagram" className="form-label">Instagram URL</label>
              <input
                id="instagram"
                type="url"
                className="form-input"
                value={formData.instagram}
                onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                placeholder="https://instagram.com/yourclub"
              />
            </div>
          </div>

            <div className="settings-section">
              <h2 className="section-title">Subscription</h2>
              <p className="form-hint">Manage your AllGrassroots plan and billing.</p>

              <div className="subscription-status-area">
                <div className="subscription-info-row">
                  <div className="subscription-plan-display">
                    <span className="subscription-plan-label">Current Plan</span>
                    <span className={`subscription-plan-name ${subscription?.plan || plan}`}>
                      {(subscription?.plan || plan || 'free').charAt(0).toUpperCase() + (subscription?.plan || plan || 'free').slice(1)}
                    </span>
                  </div>
                  {subscription?.subscriptionStatus && (
                    <div className="subscription-plan-display">
                      <span className="subscription-plan-label">Status</span>
                      <span className={`status-badge ${subscription.subscriptionStatus === 'active' ? 'status-success' : subscription.subscriptionStatus === 'past_due' ? 'status-warning' : ''}`}>
                        {subscription.subscriptionStatus === 'active' ? 'Active' : subscription.subscriptionStatus === 'past_due' ? 'Past Due' : subscription.subscriptionStatus}
                      </span>
                    </div>
                  )}
                  {subscription?.currentPeriodEnd && (
                    <div className="subscription-plan-display">
                      <span className="subscription-plan-label">Renews</span>
                      <span className="subscription-plan-value">
                        {formatDate(subscription.currentPeriodEnd)}
                      </span>
                    </div>
                  )}
                </div>

                <div className="subscription-actions">
                  {subscription?.hasSubscription ? (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleManageSubscription}
                      disabled={isManagingSub}
                    >
                      {isManagingSub ? 'Redirecting...' : 'Manage Subscription'}
                    </button>
                  ) : (
                    <a href="/upgrade" className="btn btn-primary" style={{ textDecoration: 'none' }}>
                      Upgrade Plan
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div className="settings-section">
              <h2 className="section-title">Payments</h2>
              <p className="form-hint">
                Connect your Stripe account to accept payments in your club shop.
                {isFree
                  ? ' A 5% platform fee applies on the Free plan (1.5% on Club and Pro).'
                  : ' A 1.5% platform fee applies to each transaction.'}
              </p>

              {stripeError && <div className="error-message">{stripeError}</div>}
              {stripeMessage && <div className="success-message">{stripeMessage}</div>}

              <div className="stripe-status-area">
                {!stripeStatus || !stripeStatus.connected ? (
                  /* Not connected */
                  <div className="stripe-not-connected">
                    <div className="stripe-icon">
                      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="40" height="40" rx="8" fill="#635BFF"/>
                        <path d="M18.5 16.5c0-1.1.9-1.5 2.4-1.5 2.1 0 4.8.6 6.9 1.8V11c-2.3-.9-4.6-1.3-6.9-1.3-5.6 0-9.4 3-9.4 7.9 0 7.7 10.6 6.5 10.6 9.8 0 1.3-1.1 1.7-2.7 1.7-2.3 0-5.3-.9-7.6-2.3v5.9c2.6 1.1 5.2 1.6 7.6 1.6 5.8 0 9.7-2.8 9.7-7.9-.1-8.3-10.6-6.8-10.6-9.9z" fill="white"/>
                      </svg>
                    </div>
                    <div>
                      <p style={{ marginBottom: 8, fontWeight: 500 }}>Connect your bank account to receive payments</p>
                      <p className="form-hint" style={{ margin: 0 }}>
                        You'll be redirected to Stripe to securely set up your account.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleConnectStripe}
                      disabled={isConnecting}
                      style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}
                    >
                      {isConnecting ? 'Redirecting...' : 'Connect Stripe'}
                    </button>
                  </div>
                ) : stripeStatus.connected && !stripeStatus.detailsSubmitted ? (
                  /* Connected but onboarding incomplete */
                  <div className="stripe-incomplete">
                    <div className="status-badge status-warning">Setup Incomplete</div>
                    <p style={{ margin: '8px 0' }}>
                      Your Stripe account has been created but setup is not complete.
                      Please finish entering your details to start accepting payments.
                    </p>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleCompleteStripeSetup}
                      disabled={isConnecting}
                    >
                      {isConnecting ? 'Redirecting...' : 'Complete Setup'}
                    </button>
                  </div>
                ) : stripeStatus.connected && !stripeStatus.chargesEnabled ? (
                  /* Details submitted but charges not yet enabled (verification pending) */
                  <div className="stripe-pending">
                    <div className="status-badge status-warning">Verification Pending</div>
                    <p style={{ margin: '8px 0' }}>
                      Your details have been submitted. Stripe is reviewing your account.
                      This usually takes a few minutes but can take up to a few days.
                    </p>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={loadStripeStatus}
                    >
                      Refresh Status
                    </button>
                  </div>
                ) : (
                  /* Fully connected and active */
                  <div className="stripe-connected">
                    <div className="status-badge status-success">Connected</div>
                    <p style={{ margin: '8px 0' }}>
                      Your Stripe account is active and ready to accept payments.
                      {stripeStatus.payoutsEnabled
                        ? ' Payouts to your bank account are enabled.'
                        : ' Payouts are being set up.'}
                    </p>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleViewStripeDashboard}
                    >
                      Manage on Stripe →
                    </button>
                  </div>
                )}
              </div>
            </div>

          {/* Integrations, Google Analytics is Club+ per the pricing page.
              Hidden entirely for Free admins. Server-side enforcement is
              still missing (see docs/operations/tests-needed.md item "Plan
              gating gaps"); this is UI-side only for now. */}
          {!isFree && (
            <div className="settings-section">
              <h2 className="section-title">Integrations</h2>

              <div className="form-group">
                <label htmlFor="gaTrackingId" className="form-label">
                  Google Analytics Tracking ID
                  <span className="ga-help-trigger" tabIndex={0} title="How to find your GA4 Measurement ID" onClick={() => {
                    const el = document.getElementById('ga-help');
                    if (el) el.style.display = el.style.display === 'none' ? 'block' : 'none';
                  }}>?</span>
                </label>
                <input
                  id="gaTrackingId"
                  type="text"
                  className="form-input"
                  value={formData.gaTrackingId}
                  onChange={(e) => setFormData({ ...formData, gaTrackingId: e.target.value })}
                  placeholder="G-XXXXXXXXXX"
                />
                <p className="form-hint">Paste your GA4 measurement ID to track website visitors</p>
                <div id="ga-help" style={{ display: 'none', background: 'var(--glass-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px', marginTop: '8px', fontSize: '0.85rem', lineHeight: '1.6' }}>
                  <strong>How to get your Google Analytics ID:</strong>
                  <ol style={{ margin: '8px 0 0 20px', padding: 0 }}>
                    <li>Go to <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-green)' }}>analytics.google.com</a> and sign in</li>
                    <li>Click <strong>Admin</strong> (gear icon, bottom-left)</li>
                    <li>Under <em>Property</em>, click <strong>Data Streams</strong></li>
                    <li>Click your web stream (or create one with your club URL)</li>
                    <li>Copy the <strong>Measurement ID</strong>, it starts with <code>G-</code></li>
                    <li>Paste it above and hit Save</li>
                  </ol>
                  <p style={{ marginTop: '8px', color: 'var(--text-secondary)' }}>
                    Don't have Google Analytics? <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-green)' }}>Create a free account</a>, it takes 2 minutes.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Brand colours, FREE for all tiers. Admin picks Primary +
              Secondary; server derives light/dark variants automatically.
              The contrast-clamp in the website runtime nudges pale
              colours to readable text-on-white at display time, but the
              real fix is here: the admin picking sensible colours. */}
          <div className="settings-section">
            <h2 className="section-title">Brand colours</h2>
            <p className="section-subtitle">
              Pick your club's two main colours. We'll use them across your website, emails, and buttons.
            </p>

            {coloursError && <div className="error-message">{coloursError}</div>}
            {coloursSuccess && <div className="success-message">{coloursSuccess}</div>}

            <div className="colour-editor">
              {/* Primary picker */}
              <div className="colour-row">
                <label className="colour-row-label">Primary</label>
                <div className="colour-row-controls">
                  <input
                    type="color"
                    className="colour-input-picker"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    aria-label="Primary colour picker"
                  />
                  <input
                    type="text"
                    className="form-input colour-input-hex"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value.trim())}
                    placeholder="#1256aa"
                    maxLength={7}
                    aria-label="Primary colour hex"
                  />
                </div>
                <p className="colour-hint" style={{ color: contrastBadge(primaryColor).color }}>
                  {HEX_RE.test(primaryColor) ? contrastBadge(primaryColor).label : 'Enter a valid hex'}
                </p>
              </div>

              {/* Secondary picker */}
              <div className="colour-row">
                <label className="colour-row-label">Secondary</label>
                <div className="colour-row-controls">
                  <input
                    type="color"
                    className="colour-input-picker"
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    aria-label="Secondary colour picker"
                  />
                  <input
                    type="text"
                    className="form-input colour-input-hex"
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value.trim())}
                    placeholder="#1a1a1a"
                    maxLength={7}
                    aria-label="Secondary colour hex"
                  />
                </div>
                <p className="colour-hint" style={{ color: contrastBadge(secondaryColor).color }}>
                  {HEX_RE.test(secondaryColor) ? contrastBadge(secondaryColor).label : 'Enter a valid hex'}
                </p>
              </div>

              {/* Preview */}
              <div className="colour-preview">
                <div className="colour-preview-label">Preview</div>
                <div className="colour-preview-card">
                  <div className="colour-preview-band-primary" style={{ background: primaryColor }}>
                    <span style={{ color: contrastVsWhite(primaryColor) >= 4.5 ? '#ffffff' : '#1a1a1a' }}>
                      Your club name on Primary
                    </span>
                  </div>
                  <div className="colour-preview-band-secondary" style={{ background: secondaryColor }}>
                    <span style={{ color: contrastVsWhite(secondaryColor) >= 4.5 ? '#ffffff' : '#1a1a1a' }}>
                      Headlines on Secondary
                    </span>
                  </div>
                  <div className="colour-preview-buttons">
                    <button type="button"
                      className="colour-preview-btn"
                      style={{
                        background: primaryColor,
                        color: contrastVsWhite(primaryColor) >= 4.5 ? '#ffffff' : '#1a1a1a',
                      }}
                    >Primary button</button>
                    <button type="button"
                      className="colour-preview-btn colour-preview-btn-outline"
                      style={{
                        // Guarded like the siblings above, a near-white
                        // brand colour would render an invisible outline
                        // button on the white preview card.
                        color: contrastVsWhite(primaryColor) >= 3 ? primaryColor : '#555555',
                        borderColor: contrastVsWhite(primaryColor) >= 3 ? primaryColor : '#555555',
                      }}
                    >Outline button</button>
                  </div>
                </div>
              </div>

              <div className="colour-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveColours}
                  disabled={coloursSaving || coloursResetting}
                >
                  {coloursSaving ? 'Saving…' : saveButtonLabel('Save colours')}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleResetColoursToBadge}
                  disabled={coloursSaving || coloursResetting || !hasBadge}
                  title={hasBadge
                    ? 'Re-extract colours from your badge image'
                    : 'Upload a badge first to enable this'}
                >
                  {coloursResetting ? 'Resetting…' : '↺ Reset to badge colours'}
                </button>
              </div>
            </div>
          </div>

          {/* Theme picker, Pro tier only. Server-side gate in
              lambda/routes/settings.ts rejects non-Pro requests with a
              403 even if the UI is bypassed.
              We gate on the plan returned by `getSettings` (fresh from
              DDB) rather than `useAuth().plan` (cached in localStorage
              and only refreshed when Sidebar mounts), otherwise a
              user who upgrades to Pro and navigates straight to
              Settings sees stale `club` and the picker stays hidden. */}
          {settings?.plan === 'pro' && (
            <div className="settings-section">
              <h2 className="section-title">Website Theme</h2>
              <p className="section-subtitle">
                Choose how your public website looks. Changes apply on the next page load.
              </p>

              {themeError && <div className="error-message">{themeError}</div>}
              {themeSuccess && <div className="success-message">{themeSuccess}</div>}

              <div className="theme-picker-grid">
                {/* Classic = remove themeName from DDB → website renders
                    with default Liquid Glass styling. The picker stores
                    `null` to represent this. */}
                <button
                  type="button"
                  className={`theme-picker-card ${formData.themeName === null ? 'selected' : ''}`}
                  onClick={() => handleSaveTheme(null)}
                  disabled={themeSaving}
                >
                  <div className="theme-picker-preview theme-preview-classic">
                    <span className="theme-preview-label">Classic</span>
                  </div>
                  <div className="theme-picker-info">
                    <h3>Classic</h3>
                    <p>The clean, content-first look. Subtle glass effects, balanced typography. The safe choice, works for any club.</p>
                  </div>
                  {formData.themeName === null && (
                    <div className="theme-picker-current">✓ Active</div>
                  )}
                </button>

                <button
                  type="button"
                  className={`theme-picker-card ${formData.themeName === 'matchday' ? 'selected' : ''}`}
                  onClick={() => handleSaveTheme('matchday')}
                  disabled={themeSaving}
                >
                  <div className="theme-picker-preview theme-preview-matchday">
                    <span className="theme-preview-label">Matchday</span>
                  </div>
                  <div className="theme-picker-info">
                    <h3>Matchday</h3>
                    <p>Bold, immersive design with parallax hero. Built for clubs that want a striking, fan-first feel.</p>
                  </div>
                  {formData.themeName === 'matchday' && (
                    <div className="theme-picker-current">✓ Active</div>
                  )}
                </button>

                {/* Newsfeed, Cambridge-style light, news-first layout.
                    Looks best on clubs that have News enabled and a
                    hero photo set. The "relevance gate" (greying out
                    when those aren't met) is a backlog item, for now,
                    admins can pick it freely and see what they get. */}
                <button
                  type="button"
                  className={`theme-picker-card ${formData.themeName === 'newsfeed' ? 'selected' : ''}`}
                  onClick={() => handleSaveTheme('newsfeed')}
                  disabled={themeSaving}
                >
                  <div className="theme-picker-preview theme-preview-newsfeed">
                    <span className="theme-preview-label">Newsfeed</span>
                  </div>
                  <div className="theme-picker-info">
                    <h3>Newsfeed</h3>
                    <p>Cambridge-style light layout. Featured story + small-card grid, premium typography. Built for clubs that publish regularly.</p>
                  </div>
                  {formData.themeName === 'newsfeed' && (
                    <div className="theme-picker-current">✓ Active</div>
                  )}
                </button>
              </div>

              <p className="form-hint" style={{ marginTop: 12 }}>
                <a href={`https://${clubId}.${PLATFORM_DOMAIN}/`} target="_blank" rel="noopener noreferrer">
                  View your live website →
                </a>
              </p>
            </div>
          )}

          <div className="settings-section">
            <h2 className="section-title">Sport</h2>

            <div className="form-group">
              <label htmlFor="sport">Your club's sport</label>
              <select
                id="sport"
                value={formData.sport}
                onChange={(e) => setFormData({ ...formData, sport: e.target.value as SportId })}
              >
                {(sports.length ? sports : [{ id: formData.sport, label: formData.sport }]).map(s => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
              <p className="form-hint">
                Sets the positions you can give players, the roles on your Club
                Staff page, how scores are labelled and how seasons are named.
                Changing it doesn't alter anything you've already saved.
              </p>
            </div>
          </div>

          <div className="settings-section">
            <h2 className="section-title">Teams</h2>

            <div className="form-group">
              <label htmlFor="teamCardCoaches">Team cards on your website</label>
              <select
                id="teamCardCoaches"
                value={formData.teamCardCoaches}
                onChange={(e) => setFormData({ ...formData, teamCardCoaches: e.target.value as 'all' | 'lead' })}
              >
                <option value="all">Full, every coach and the team description</option>
                <option value="lead">Compact, lead coach and key details only</option>
              </select>
              <p className="form-hint">
                Applies to every team. Both styles show the team's league, match
                day and training times. Full adds every coach, any email they've
                agreed to publish, and the team's description. Compact shows one
                coach, the most senior by title, so a Manager outranks an
                Assistant Manager, and leaves the emails, the other coaches and
                the description to the team's own page, which keeps a long list
                of teams readable. Nothing is hidden from your website either
                way.
              </p>
            </div>
          </div>

          <div className="settings-section">
            <h2 className="section-title">Site Status</h2>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.maintenanceMode}
                  onChange={(e) => setFormData({ ...formData, maintenanceMode: e.target.checked })}
                />
                <span className="checkbox-text">Enable Maintenance Mode</span>
              </label>
              <p className="form-hint">
                When enabled, the public website will display a maintenance message.
              </p>
            </div>
          </div>

          <div className="settings-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : saveButtonLabel('Save Settings')}
            </button>
          </div>
        </form>

        {/* ── CHANGE PASSWORD ───────────────────────── */}
        <form
          id="change-password-section"
          onSubmit={handleChangePassword}
          className="settings-form"
          style={{ marginTop: 24 }}
        >
          <div className="settings-section">
            <h2 className="section-title">
              Change Password
              {forcePasswordChange && (
                <span className="admin-required-badge">Required</span>
              )}
            </h2>
            {forcePasswordChange && (
              <p className="form-hint" style={{ color: '#b45309', fontWeight: 500 }}>
                You were given a temporary password. Please set a new permanent password now.
              </p>
            )}

            {passwordError && <div className="error-message">{passwordError}</div>}
            {passwordSuccess && <div className="success-message">{passwordSuccess}</div>}

            <div className="form-group">
              <label htmlFor="currentPassword" className="form-label">Current Password</label>
              <input
                id="currentPassword"
                type="password"
                className="form-input"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword" className="form-label">New Password</label>
              <input
                id="newPassword"
                type="password"
                className="form-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
              <p className="form-hint">Must be at least 8 characters.</p>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">Confirm New Password</label>
              <input
                id="confirmPassword"
                type="password"
                className="form-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>
          </div>

          <div className="settings-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isChangingPassword}
            >
              {isChangingPassword ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>

        {/* ── ADMIN USERS ───────────────────────────── */}
        <div className="settings-form" style={{ marginTop: 24 }}>
          <div className="settings-section" style={{ borderBottom: 'none', paddingBottom: 0, marginBottom: 0 }}>
            <div className="admin-users-header">
              <div>
                <h2 className="section-title" style={{ marginBottom: 4 }}>Admin Users</h2>
                <p className="form-hint" style={{ marginTop: 0 }}>
                  People who can log in and manage this club's dashboard.
                  {adminUsers && (
                    <span className="admin-limit-badge">
                      {adminCount} / {adminLimit === 1 ? '1 (Free)' : `${adminLimit} (${(adminUsers.plan.charAt(0).toUpperCase() + adminUsers.plan.slice(1))})`}
                    </span>
                  )}
                </p>
              </div>
              {isOwner && (
                <div className="admin-invite-action">
                  {isFree ? (
                    <a href="/upgrade" className="btn btn-secondary" style={{ textDecoration: 'none', fontSize: '13px' }}>
                      Upgrade to Invite
                    </a>
                  ) : atAdminLimit ? (
                    <a href="/upgrade" className="btn btn-secondary" style={{ textDecoration: 'none', fontSize: '13px' }}>
                      Upgrade for More
                    </a>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => { setShowInviteModal(true); setAdminError(''); setAdminSuccess(''); }}
                    >
                      Invite Admin
                    </button>
                  )}
                </div>
              )}
            </div>

            {adminError && <div className="error-message" style={{ marginTop: 12 }}>{adminError}</div>}
            {adminSuccess && <div className="success-message" style={{ marginTop: 12 }}>{adminSuccess}</div>}

            {/* Plan limit info bar */}
            {isOwner && isFree && (
              <div className="admin-plan-notice">
                <span>Your Free plan supports 1 admin only.</span>
                <a href="/upgrade" style={{ color: 'var(--primary-green)', fontWeight: 600, marginLeft: 8 }}>
                  Upgrade to Club or Pro &rarr;
                </a>
              </div>
            )}
            {isOwner && !isFree && atAdminLimit && (
              <div className="admin-plan-notice admin-plan-notice--warning">
                <span>You've reached the {adminLimit}-admin limit on the {(adminUsers?.plan || plan).charAt(0).toUpperCase() + (adminUsers?.plan || plan).slice(1)} plan.</span>
                <a href="/upgrade" style={{ color: 'var(--primary-green)', fontWeight: 600, marginLeft: 8 }}>
                  Upgrade for more &rarr;
                </a>
              </div>
            )}

            {isLoadingAdmins ? (
              <div className="admin-users-loading">Loading team members...</div>
            ) : (
              <div className="admin-users-list">
                {(adminUsers?.admins || []).map((admin: AdminUser) => (
                  <div key={admin.email} className="admin-user-row">
                    <div className="admin-user-avatar">
                      {(admin.name || admin.email).charAt(0).toUpperCase()}
                    </div>
                    <div className="admin-user-info">
                      <div className="admin-user-name">
                        {admin.name || admin.email}
                        {admin.email === currentUserEmail && (
                          <span className="admin-you-badge">You</span>
                        )}
                      </div>
                      <div className="admin-user-email">{admin.email}</div>
                      {admin.forcePasswordChange && (
                        <div className="admin-pending-badge">Password change pending</div>
                      )}
                    </div>
                    <div className="admin-user-meta">
                      <span className={`admin-role-badge admin-role-${admin.role}`}>
                        {admin.role === 'owner' ? 'Owner' : 'Admin'}
                      </span>
                      {admin.createdAt && (
                        <span className="admin-user-date">Added {formatDateShort(admin.createdAt)}</span>
                      )}
                    </div>
                    {isOwner && admin.email !== currentUserEmail && admin.role !== 'owner' && (
                      <button
                        type="button"
                        className="admin-remove-btn"
                        onClick={() => handleRemoveAdmin(admin.email)}
                        disabled={removingEmail === admin.email}
                        title={`Remove ${admin.name || admin.email}`}
                      >
                        {removingEmail === admin.email ? 'Removing...' : 'Remove'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <aside className="settings-info">
          <div className="info-card">
            <h3 className="info-title">Last Updated</h3>
            <p className="info-value">
              {settings?.updatedAt ? formatDate(settings.updatedAt) : 'Never'}
            </p>
          </div>

          <div className="info-card">
            <h3 className="info-title">Quick Links</h3>
            <div className="quick-links">
              <a
                href={`https://${clubId}.${PLATFORM_DOMAIN}`}
                target="_blank"
                rel="noopener noreferrer"
                className="quick-link"
              >
                View Your Website &rarr;
              </a>
            </div>
          </div>

          <div className="info-card">
            <h3 className="info-title">Help &amp; Support</h3>
            <p className="info-text">
              Need help? Contact us at <a href="mailto:hello@allgrassroots.com" style={{ color: 'var(--primary-green)' }}>hello@allgrassroots.com</a>
            </p>
          </div>
        </aside>
      </div>

      {/* ── INVITE MODAL ─────────────────────────── */}
      {showInviteModal && (
        <div className="admin-modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2 className="admin-modal-title">Invite Admin</h2>
              <button
                type="button"
                className="admin-modal-close"
                onClick={() => setShowInviteModal(false)}
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <p className="form-hint" style={{ marginBottom: 20 }}>
              They'll receive an email with a temporary password and a link to log in.
              They'll be asked to change their password on first login.
            </p>

            {adminError && <div className="error-message">{adminError}</div>}

            <form onSubmit={handleInviteAdmin}>
              <div className="form-group">
                <label htmlFor="inviteName" className="form-label">Full Name</label>
                <input
                  id="inviteName"
                  type="text"
                  className="form-input"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  required
                  placeholder="e.g. Sarah Smith"
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="inviteEmail" className="form-label">Email Address</label>
                <input
                  id="inviteEmail"
                  type="email"
                  className="form-input"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                  placeholder="sarah@yourclub.com"
                />
              </div>

              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowInviteModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isInviting}
                >
                  {isInviting ? 'Sending invite...' : 'Send Invite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── DANGER ZONE, export + self-service deletion (owner only) ── */}
      {isOwnerFromJwt && (
        <div className="settings-form" style={{ marginTop: 24 }}>
          <div className="settings-section" style={{ border: '1px solid #fca5a5', borderRadius: 8, padding: 16 }}>
            <h2 className="section-title" style={{ color: '#b91c1c' }}>Danger Zone</h2>

            <div className="form-group">
              <p className="form-hint" style={{ marginBottom: 8 }}>
                Download everything we hold for your club, content, teams,
                players, members, orders and more, as a single JSON file.
              </p>
              <button type="button" className="btn btn-outline" onClick={handleExportData} disabled={isExporting}>
                {isExporting ? 'Preparing…' : 'Download my data (JSON)'}
              </button>
            </div>

            <div className="form-group" style={{ marginTop: 16 }}>
              {!deleteArmed ? (
                <>
                  <p className="form-hint" style={{ marginBottom: 8 }}>
                    Permanently delete your club account, website and all data.
                  </p>
                  <button
                    type="button"
                    className="btn btn-outline"
                    style={{ color: '#b91c1c', borderColor: '#fca5a5' }}
                    onClick={() => setDeleteArmed(true)}
                  >
                    Delete my account…
                  </button>
                </>
              ) : (
                <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: 14 }}>
                  <p style={{ margin: '0 0 8px', fontWeight: 600, color: '#b91c1c' }}>
                    Are you sure? This is NOT reversible.
                  </p>
                  <p className="form-hint" style={{ marginBottom: 10 }}>
                    Your website goes offline immediately and your account, content,
                    teams, players, members and images are permanently deleted.
                    Consider downloading your data first. To confirm, type
                    {' '}<strong>DELETE</strong> below.
                  </p>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Type DELETE to confirm"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    style={{ maxWidth: 260, marginBottom: 10 }}
                  />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      style={{ background: '#b91c1c' }}
                      disabled={deleteConfirmText !== 'DELETE' || isDeletingAccount}
                      onClick={handleDeleteAccount}
                    >
                      {isDeletingAccount ? 'Deleting…' : 'Permanently delete my account'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline"
                      disabled={isDeletingAccount}
                      onClick={() => { setDeleteArmed(false); setDeleteConfirmText(''); setDangerError(''); }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {dangerError && <p style={{ color: '#b91c1c', marginTop: 8 }}>{dangerError}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Settings;
